

import { useRef, useState, useEffect } from "react";
import { useFormContext, Controller } from "react-hook-form";
import CircularProgress from "./CircularProgress";
import { GetImages } from "../../utils/GetImages";
import {
  getFileExtension,
  JPEG_PNG_EXTENSION_ERROR,
} from "./imageUploadConstants";
import "./FileUploader.css";

interface UploaderProps {
  name: string;
  required?: boolean;
  requiredMessage?: string;
  allowedFormats?: string[];
  /** When set, file extension is validated in addition to MIME type (e.g. block .jfif). */
  allowedExtensions?: string[];
  maxSizeMB?: number;
  defaultImage?: string; // Base64 URL or pure base64 data
  resetTrigger?: number;
}

const Uploader = ({
  name,
  required = false,
  requiredMessage = "This field is required",
  allowedFormats = ["image/jpeg", "image/png", "image/jpg", "image/webp"],
  allowedExtensions,
  maxSizeMB = 1,
  defaultImage,
  resetTrigger,
}: UploaderProps) => {
  const { control, setValue, setError, clearErrors, formState: { errors } } = useFormContext();
  const [mainImage, setMainImage] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewImg, setPreviewImg] = useState<string | null>(null);
  const initializedRef = useRef(false);
  /** Sticky reject message shown next to the field. A rejected selection is never
   *  committed, so this alone only blocks Submit when the field is required -- an
   *  optional field may still submit empty despite a lingering rejection message. */
  const [rejectionError, setRejectionError] = useState<string | null>(null);

  // Normalize base64 to data URL
  const normalizeBase64Url = (input: string) => {
    if (input.startsWith('data:')) return input;
    if (input.startsWith('iVBORw0KGgo=')) return `data:image/png;base64,${input}`;
    if (input.startsWith('/9j/')) return `data:image/jpeg;base64,${input}`;
    return `data:image/png;base64,${input}`; // Default PNG
  };

  // Convert any base64 to PNG data URL using canvas
  const convertToPNGDataURL = (base64Input: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png')); // Force PNG
        } else {
          reject(new Error('Canvas context not available'));
        }
      };
      img.onerror = reject;
      img.src = normalizeBase64Url(base64Input);
    });
  };

  // Helper to convert base64 to PNG File object
  const base64ToPNGFile = async (base64Input: string, fileName: string = 'image.png'): Promise<File | null> => {
    try {
      const pngDataUrl = await convertToPNGDataURL(base64Input);
      const base64Data = pngDataUrl.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      return new File([blob], fileName, { type: 'image/png' });
    } catch (error) {
      console.error('Base64 to PNG conversion error:', error);
      return null;
    }
  };

  // Initialize with defaultImage
  useEffect(() => {
    if (!defaultImage || initializedRef.current) return; // prevent re-run
    initializedRef.current = true; // mark initialized
    const normalized = normalizeBase64Url(defaultImage);
    setPreviewImg(normalized);
    base64ToPNGFile(defaultImage, "default.png").then((file) => {
      if (file && allowedFormats.includes("image/png")) {
        setValue(name, file, { shouldValidate: true });
        setMainImage(file);
      }
    });
  }, [defaultImage, name, setValue, allowedFormats]);

  // Reset on trigger
  useEffect(() => {
    if (resetTrigger !== undefined) {
      setMainImage(null);
      setPreviewImg(null);
      setRejectionError(null);
      initializedRef.current = false; // Reset initialized flag
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [resetTrigger]);

  const clearProgress = () => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return prev;
        }
        return prev + 10;
      });
    }, 100);
  };

  const acceptAttribute = allowedExtensions?.length
    ? [...allowedExtensions, ...allowedFormats].join(",")
    : allowedFormats.join(",");

  const validateFile = (file: File | string) => {
    if (file instanceof File) {
      if (!allowedFormats.includes(file.type)) {
        return `Only ${allowedFormats.join(", ")} allowed`;
      }
      if (allowedExtensions?.length) {
        const extension = getFileExtension(file.name);
        if (!allowedExtensions.includes(extension)) {
          return JPEG_PNG_EXTENSION_ERROR;
        }
      }
      const sizeInMB = file.size / (1024 * 1024);
      if (sizeInMB > maxSizeMB) {
        return `Max file size is ${maxSizeMB}MB`;
      }
      return true;
    }

    if (typeof file === 'string') {
      const normalized = normalizeBase64Url(file);
      const mimeMatch = normalized.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+);base64,/);
      const mimeType = mimeMatch ? mimeMatch[1] : "image/png"; // Assume PNG now

      if (!allowedFormats.includes(mimeType)) {
        return `Only ${allowedFormats.join(", ")} allowed`;
      }

      let base64Data = normalized.split(',')[1] || normalized;
      const sizeInBytes = Math.floor((base64Data.length * 3) / 4);
      const sizeInMB = sizeInBytes / (1024 * 1024);
      if (sizeInMB > maxSizeMB) {
        return `Max file size is ${maxSizeMB}MB`;
      }
      return true;
    }

    return `Invalid file format`;
  };

  const handleFileChange = async (file: File, fieldOnChange: (value: File | null) => void) => {
    const result = validateFile(file);
    if (result !== true) {
      // Keep any existing valid image -- a rejected replacement is never committed to
      // mainImage, so re-validating the already-committed value in the Controller's
      // validate rule would be redundant (mirrors the same rationale documented in
      // MultipleFileUploader.tsx's Controller rules comment).
      setRejectionError(result);
      setError(name, { type: "manual", message: result });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setRejectionError(null);
    setLoading(true);
    clearProgress();

    // Convert uploaded file to PNG preview
    const previewUrl = URL.createObjectURL(file);

setPreviewImg(previewUrl);
setMainImage(file);

fieldOnChange(file);
clearErrors(name);

setLoading(false);
setProgress(100);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = (onChange: (value: File | null) => void) => {
    setMainImage(null);
    setPreviewImg(null);
    setProgress(0);
    setRejectionError(null);
    onChange(null);
    clearErrors(name);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

 const img = previewImg; 

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        // A rejected selection is never committed to mainImage (handleFileChange
        // returns before touching it), so the only thing left to validate here is
        // whatever was already committed -- there's nothing left for a rejectionError
        // check to add: an optional field is fine empty, and a required field with no
        // committed file is already caught by the empty-value check below. Mirrors
        // MultipleFileUploader.tsx: re-running validateFile against an already-committed
        // value here would also re-reject a legacy defaultImage saved under an older,
        // larger size cap purely for being displayed -- selection-time validation in
        // handleFileChange already covers every new selection.
        validate: (file: File | string | null) => {
          if (!required) return true;
          if (!file) return requiredMessage;
          return true;
        },
      }}
      render={({ field: { onChange } }) => (
        <div className="uploaderContainer">
          <div
            className={`uploader-box-container${!img ? ' border-image-dashed' : ''}${loading ? ' uploding-bg-color' : ''}`}
          >
            <input
              type="file"
              ref={fileInputRef}
              id="upload"
              className="upload-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                handleFileChange(file, onChange);
              }}
             accept={acceptAttribute}
            />

            {loading ? (
              <div className="d-flex flex-column align-items-center gap-2">
                <CircularProgress size={30} thickness={2} color="blue" progress={progress} />
                <p className="sub_text fw-700 blue">Uploading</p>
              </div>
            ) : img ? (
              <img src={img} alt="preview" className="preview-image" />
            ) : (
              <label htmlFor="upload" className="upload-label">
                <div className="emptyImage gap-2">
                  <img className="image-icon-size" src={GetImages.BlueAdd} alt="add" />
                  <div className="sub_text fw-700 blue">Add Photo</div>
                </div>
              </label>
            )}
          </div>

          {img ? (
            <div className="deletereplacemain mt-2">
              <div onClick={() => fileInputRef.current?.click()} className="deletereplace blue-text-color">
                <img src={GetImages.BlueReplace} className="iconstyle" alt="replace" />
                <p className="replace">Replace</p>
              </div>
              <div><div className="dividerSmallDiv" /></div>
              <div onClick={() => handleDelete(onChange)} className="deletereplace blue-red-color">
                <img src={GetImages.deleteIconRed} className="iconstyle" alt="delete" />
                <p className="delete">Delete</p>
              </div>
            </div>
          ) : (
            <p className="sub_text grey_subtext1 mt-3 mb-2">
              Image format jpeg and png. Max image size {maxSizeMB} MB
            </p>
          )}

          {(errors[name] || rejectionError) && (
            <div className="invalid-feedback d-block">
              {rejectionError ?? (errors[name]?.message as string)}
            </div>
          )}
        </div>
      )}
    />
  );
};

export default Uploader;